<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RobotVisualizer } from '@therobotcompany/robot-visualizer';
import gettext from '@/i18n/gettext';
import { MODULES_BASE_URL } from '@/Constants';
import type { IRobotFinderResult } from '@/models/RobotFinder';

const { $gettext } = gettext;

const props = withDefaults(
  defineProps<{
    data: IRobotFinderResult;
    name: string;
    showInColumns?: boolean;
  }>(),
  {
    showInColumns: false,
  },
);

const robotLoaded = ref(false);

const moduleNamesArray = computed(() => {
  if (!props.data.module_names) {
    return [];
  }
  return props.data.module_names.split(', ');
});

const consolidatedResultModules = computed(() => {
  return moduleNamesArray.value.reduce(
    (acc, curr) => {
      acc[curr] = (acc[curr] || 0) + 1;
      return acc;
    },
    {} as { [key: string]: number },
  );
});

const numberOfClamps = computed(() => {
  return props.data.module_ids.filter((id) => id >= 8000 && id < 9000).length;
});

const jointAngles = ref<number[]>([]);

watch(
  () => props.data.display_pose,
  (newAngles: number[]) => {
    jointAngles.value = [...newAngles];
  },
  { immediate: true },
);
</script>
<template>
  <v-row class="mx-0">
    <v-col v-if="props.showInColumns" cols="12">
      <h1 class="mb-2">{{ name }}</h1>
    </v-col>
    <v-col
      :cols="props.showInColumns ? 12 : 8"
      :order="props.showInColumns ? 1 : 2"
    >
      <div class="h-100 position-relative" style="min-height: 300px">
        <robot-visualizer
          background-color="#fff"
          :show-axes-helper="false"
          :show-grid="false"
          :modules-base-url="MODULES_BASE_URL"
          :module-ids="
            data.module_ids.map((id) => id.toString().padStart(4, '0'))
          "
          :current-angles="data.display_pose"
          :demoAnimationOnHover="true"
          @onRobotLoadedChanged="(loaded) => (robotLoaded = loaded)"
        >
        </robot-visualizer>
        <div
          v-if="robotLoaded"
          style="top: 8px; right: 8px; z-index: 2051; position: absolute"
        >
          <v-btn
            size="small"
            icon="mdi-arrow-expand"
            :href="`/visualizer?modules=${data.module_ids.map((id) =>
              id.toString().padStart(4, '0'),
            )}&angles=${jointAngles}`"
            target="_blank"
            rel="noopener noreferrer"
          />
        </div>
      </div>
    </v-col>
    <!--    <h2 class="mb-5 mt-12 text-center">{{ name }}: {{ data.price }}</h2>-->
    <v-col
      :cols="props.showInColumns ? 12 : 4"
      :order="props.showInColumns ? 2 : 1"
    >
      <div v-if="!props.showInColumns">
        <h1 class="mb-8">{{ name }}</h1>
      </div>
      <ul class="mx-6">
        <li>
          {{ $gettext('Maximum Payload') }}:
          <b>{{ data.payload.toFixed(2) }}{{ $gettext('kg') }}</b>
        </li>
        <li>
          {{ $gettext('Maximum Reach') }}:
          <b>
            {{ parseFloat((data.reach * 1000).toFixed(0)) }}
            {{ $gettext('mm') }}
          </b>
        </li>
        <li v-if="data.module_names">
          {{ $gettext('Modules') }}: <b>{{ data.module_names }}</b>
        </li>
        <li>
          {{ $gettext('The package includes') }}:
          <!--          {{ $gettext('The total price of') }}-->
          <!--          <b>{{ data.price }}</b>-->
          <!--          {{ $gettext('includes') }}:-->
        </li>

        <li style="list-style-type: none">
          <ul class="ml-6">
            <li>
              {{ moduleNamesArray.length }}
              {{ $gettext('Robot Modules') }}:
            </li>
            <li style="list-style-type: none">
              <ul class="ml-6">
                <li
                  v-for="(count, name) in consolidatedResultModules"
                  :key="name"
                >
                  {{ count }}x {{ name }}
                </li>
              </ul>
            </li>
            <li>
              {{ numberOfClamps }}x
              {{ $gettext('Module Clamps') }}
            </li>
            <li>1x {{ $gettext('Control Unit') }}</li>
            <li>1x {{ $gettext('Cable Set') }}</li>
            <li>1x {{ $gettext('LTE Module') }}</li>
            <li>
              1x {{ $gettext('Basic Software Package') }}
              {{ $gettext('for 24 months') }}
            </li>
          </ul>
        </li>
      </ul>
    </v-col>
  </v-row>
</template>

<style lang="scss" scoped>
ul li {
  margin-top: 5px;
}
</style>
